import { useState } from 'react'

import { Text } from '@vtex/shoreline'

import { Button } from '~/components/Buttons'
import { isReadOnly } from '~/helpers'
import { useSidePanel } from '~/hooks/useSidePanel'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import { OptionSelect } from '../../../components/advertiserBrand/OptionSelect'
import { ProductThumb } from '../../../components/advertiserBrand/ProductThumb'
import styles from '../../../components/advertiserBrand/styles.module.css'
import { productCode, toIdentities } from '../../../helpers/gtin'
import { useAdvertiserBrandProductMutations } from '../../../hooks/useAdvertiserBrandProducts'
import { useAdvertiserBrandOptions } from '../../../hooks/useAdvertiserBrands'

const PANEL_ID = 'advertiser-product-detail'

type ProductDrawerContentProps = {
  product: AdvertiserBrandProduct
  name: string
  imageUrl?: string | null
}

/**
 * Detalhe de um produto: o EAN, ou o SKU com o publisher, é a identidade do
 * vínculo e não muda; a marca é reatribuída pelo mesmo verbo da ação em massa.
 */
const ProductDrawerContent: React.FC<ProductDrawerContentProps> = ({
  product,
  name,
  imageUrl
}) => {
  const { t } = useAPPTranslation()
  const { options } = useAdvertiserBrandOptions()
  const { moveProducts } = useAdvertiserBrandProductMutations()
  const { removeSidePanel } = useSidePanel()

  const [brand, setBrand] = useState(product.brand)

  const hasChanged = !!brand && brand !== product.brand
  const isSku = product.identifier_type === 'sku'

  const handleSave = async () => {
    if (!brand) return

    // decisão tomada pela pessoa na tela
    await moveProducts.mutateAsync({
      brand,
      ...toIdentities([product]),
      source: 'manual'
    })
    removeSidePanel({ id: PANEL_ID })
  }

  return (
    <div className={styles.productDrawer}>
      <ProductThumb url={imageUrl} className={styles.productDrawerThumb} />

      <h2 className={styles.productDrawerName}>{name}</h2>

      <dl className={styles.productDrawerFields}>
        <dt>
          {t(
            isSku
              ? 'rm:advertiserProducts.table.column.sku'
              : 'rm:advertiserProducts.table.column.gtin'
          )}
        </dt>
        <dd>{productCode(product)}</dd>

        {isSku && (
          <>
            <dt>{t('rm:advertiserProducts.table.column.publisher')}</dt>
            <dd>{product.publisher_name}</dd>
          </>
        )}

        <dt>{t('rm:advertiserProducts.table.column.brand')}</dt>
        <dd>
          {isReadOnly() ? (
            <Text variant="body">
              {product.brand ?? t('rm:advertiserProducts.noBrand')}
            </Text>
          ) : (
            <OptionSelect
              placeholder={t('rm:advertiserProducts.noBrand')}
              options={options}
              isClearable={false}
              value={brand}
              onChange={option => setBrand(option?.value ?? null)}
            />
          )}
        </dd>
      </dl>

      {hasChanged && (
        <div className={styles.productDrawerFooter}>
          <Button
            template="transparent"
            size="medium"
            onClick={() => setBrand(product.brand)}
          >
            {t('rm:advertiserProducts.detail.undo')}
          </Button>
          <Button
            template="primary"
            size="medium"
            loading={moveProducts.isPending}
            onClick={handleSave}
          >
            {t('rm:advertiserProducts.detail.submit')}
          </Button>
        </div>
      )}
    </div>
  )
}

/** Abre o detalhe do produto no painel lateral. */
export const useProductDrawer = () => {
  const { t } = useAPPTranslation()
  const { addSidePanel } = useSidePanel()

  return (props: ProductDrawerContentProps) =>
    addSidePanel({
      id: PANEL_ID,
      title: t('rm:advertiserProducts.detail.title'),
      type: 'content',
      size: 'small',
      content: <ProductDrawerContent {...props} />
    })
}
